import React, { useState } from 'react';
import { Grid3x3, ChevronDown, PanelRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface ViewModeSelectorProps {
    mode: 'democratic' | 'sidebar' | 'custom';
    onChange: (mode: 'democratic' | 'sidebar' | 'custom') => void;
}

export const ViewModeSelector: React.FC<ViewModeSelectorProps> = ({ mode, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);

    const handleSelect = (newMode: 'democratic' | 'sidebar') => {
        onChange(newMode);
        setIsOpen(false);
    };

    const getModeLabel = () => {
        switch (mode) {
            case 'democratic': return 'Democratic';
            case 'sidebar': return 'Sidebar';
            case 'custom': return 'Custom';
        }
    };

    return (
        <div className="relative z-[60]">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white text-sm"
            >
                <Grid3x3 className="w-4 h-4" />
                <span>{getModeLabel()}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Backdrop */}
                        <div
                            className="fixed inset-0 z-[55]"
                            onClick={() => setIsOpen(false)}
                            aria-hidden
                        />
                        
                        {/* Dropdown */}
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute bottom-full left-0 z-[60] mb-2 min-w-[160px] overflow-hidden rounded-lg border border-white/20 bg-black/90 backdrop-blur-sm"
                        >
                            <button
                                onClick={() => handleSelect('democratic')}
                                className={`w-full px-4 py-3 text-left text-sm hover:bg-white/10 transition-colors ${
                                    mode === 'democratic' ? 'bg-white/20 text-white font-medium' : 'text-white/80'
                                }`}
                            >
                                Democratic
                            </button>
                            <button
                                onClick={() => handleSelect('sidebar')}
                                className={`flex w-full items-center gap-2 px-4 py-3 text-left text-sm hover:bg-white/10 transition-colors ${
                                    mode === 'sidebar' ? 'bg-white/20 text-white font-medium' : 'text-white/80'
                                }`}
                            >
                                <PanelRight className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
                                Sidebar
                            </button>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
};
